/**
 * Cinquième écran, réservé à la direction : journal d'ingestion, fraîcheur des
 * données, anomalies détectées et réingestion manuelle.
 */

import { anciennete, entier, horodatageFr } from '@scc/core';
import { journalAdministration, relancerIngestion, type Charge } from '../api.js';
import { h, vider } from '../dom.js';
import { badge, enteteSection, noteLecture } from '../composants.js';

export function vueAdministration(d: Charge): HTMLElement {
  const corps = h('div', {}, h('p', { classe: 'note-lecture' }, 'Chargement du journal…'));
  const retourAction = h('div', { classe: 'barre-actions' });

  const bouton = h(
    'button',
    {
      classe: 'btn-action',
      type: 'button',
      // Le gestionnaire reste synchrone : un `addEventListener` ignore la
      // promesse renvoyée, et un rejet non capturé passerait inaperçu.
      surClick: (e) => {
        const b = e.currentTarget as HTMLButtonElement;
        void (async () => {
          b.disabled = true;
          b.textContent = 'Réingestion en cours…';
          vider(retourAction);
          try {
            const r = await relancerIngestion();
            retourAction.appendChild(
              h('span', { classe: 'titre' }, r.succes ? 'Réingestion réussie' : 'Réingestion partielle'),
            );
            retourAction.appendChild(h('span', { classe: 'note-lecture' }, r.message));
          } catch {
            retourAction.appendChild(h('span', { classe: 'titre' }, 'Réingestion refusée.'));
          } finally {
            b.disabled = false;
            b.textContent = 'Relancer l’ingestion';
            await charger();
          }
        })();
      },
    },
    'Relancer l’ingestion',
  );

  const charger = async (): Promise<void> => {
    try {
      const j = await journalAdministration();
      vider(corps);

      const maintenant = new Date().toISOString();
      corps.appendChild(
        h(
          'div',
          { classe: 'kpi-grid' },
          h(
            'div',
            { classe: `kpi ${j.fraicheur.modeDegrade ? 'warning' : 'progress'}` },
            h('div', { classe: 'kpi-value' }, j.fraicheur.modeDegrade ? 'Dégradé' : 'Direct'),
            h('div', { classe: 'kpi-label' }, 'Mode de service des données'),
            h(
              'span',
              { classe: `kpi-delta ${j.fraicheur.modeDegrade ? 'delta-down' : 'delta-up'}` },
              j.fraicheur.origine === 'sources' ? 'sources interrogées en direct' : 'instantané livré',
            ),
          ),
          h(
            'div',
            { classe: 'kpi neutral' },
            h(
              'div',
              { classe: 'kpi-value' },
              j.fraicheur.derniereIngestion ? anciennete(j.fraicheur.derniereIngestion, maintenant) : '—',
            ),
            h('div', { classe: 'kpi-label' }, 'Ancienneté des données'),
            h(
              'span',
              { classe: 'kpi-delta delta-neutral' },
              j.fraicheur.derniereIngestion ? horodatageFr(j.fraicheur.derniereIngestion) : 'aucune ingestion',
            ),
          ),
          h(
            'div',
            { classe: `kpi ${j.anomalies.length > 2 ? 'warning' : 'neutral'}` },
            h('div', { classe: 'kpi-value' }, entier(j.anomalies.length)),
            h('div', { classe: 'kpi-label' }, 'Anomalies signalées'),
            h('span', { classe: 'kpi-delta delta-neutral' }, 'à instruire'),
          ),
          h(
            'div',
            { classe: 'kpi neutral' },
            h('div', { classe: 'kpi-value' }, entier(j.appels.length)),
            h('div', { classe: 'kpi-label' }, 'Appels d’API journalisés'),
            h('span', { classe: 'kpi-delta delta-neutral' }, 'derniers enregistrements'),
          ),
        ),
      );

      corps.appendChild(
        h(
          'p',
          { classe: 'text-block' },
          `Jev aujourd’hui : ${entier(j.jev.appelsDuJour)} appels, ${j.jev.coutDuJour.toFixed(4)} $`,
        ),
      );

      corps.appendChild(h('h3', { classe: 'card' }, 'Anomalies détectées'));
      corps.appendChild(
        h(
          'table',
          { classe: 'comp-table' },
          h('caption', { classe: 'invisible' }, 'Anomalies détectées'),
          h(
            'thead',
            {},
            h('tr', {}, h('th', { scope: 'col' }, 'Nature'), h('th', { scope: 'col' }, 'Occurrences'), h('th', { scope: 'col' }, 'Exemples')),
          ),
          h(
            'tbody',
            {},
            ...(j.anomalies.length === 0
              ? [h('tr', {}, h('th', { scope: 'row' }, 'Aucune anomalie'), h('td', {}, '0'), h('td', {}, '—'))]
              : j.anomalies.map((a) =>
                  h(
                    'tr',
                    {},
                    h('th', { scope: 'row' }, a.libelle),
                    h('td', {}, entier(a.occurrences)),
                    h('td', {}, a.exemples.join(' · ') || '—'),
                  ),
                )),
          ),
        ),
      );

      corps.appendChild(h('h3', { classe: 'card' }, 'Campagnes d’ingestion'));
      corps.appendChild(
        h(
          'table',
          { classe: 'comp-table' },
          h('caption', { classe: 'invisible' }, 'Campagnes d’ingestion'),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Début'),
              h('th', { scope: 'col' }, 'Origine'),
              h('th', { scope: 'col' }, 'Issue'),
              h('th', { scope: 'col' }, 'Message'),
            ),
          ),
          h(
            'tbody',
            {},
            ...j.campagnes.map((c) =>
              h(
                'tr',
                {},
                h('th', { scope: 'row' }, horodatageFr(c.debut)),
                h('td', {}, c.origine),
                h('td', {}, badge(c.succes === 1 ? 'favorable' : 'alerte', c.succes === 1 ? 'Réussie' : 'En échec')),
                h('td', {}, c.message ?? '—'),
              ),
            ),
          ),
        ),
      );

      corps.appendChild(h('h3', { classe: 'card' }, 'Journal des appels d’API'));
      corps.appendChild(
        h(
          'table',
          { classe: 'comp-table' },
          h('caption', { classe: 'invisible' }, 'Journal des appels d’API'),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Horodatage'),
              h('th', { scope: 'col' }, 'Source'),
              h('th', { scope: 'col' }, 'Ressource'),
              h('th', { scope: 'col' }, 'Statut'),
              h('th', { scope: 'col' }, 'Durée'),
              h('th', { scope: 'col' }, 'Éléments'),
            ),
          ),
          h(
            'tbody',
            {},
            ...j.appels.slice(0, 60).map((a) =>
              h(
                'tr',
                {},
                h('th', { scope: 'row' }, horodatageFr(a.horodatage)),
                h('td', {}, a.source),
                h('td', {}, a.ressource),
                h(
                  'td',
                  {},
                  a.erreur
                    ? badge('alerte', String(a.statut ?? '—'))
                    : badge('favorable', String(a.statut ?? '—')),
                ),
                h('td', {}, `${entier(a.duree_ms)} ms`),
                h('td', {}, entier(a.nb_elements)),
              ),
            ),
          ),
        ),
      );
    } catch {
      vider(corps);
      corps.appendChild(
        h('div', { classe: 'erreur-bloc' }, 'Le journal d’ingestion n’a pas pu être chargé.'),
      );
    }
  };

  void charger();

  return h(
    'div',
    { classe: 'page', id: 'page-exploitation', role: 'region', 'aria-label': 'Exploitation' },
    h(
      'section',
      {},
      enteteSection(1, 'Exploitation', 'Journal d’ingestion, fraîcheur des données et anomalies'),
      h(
        'p',
        { classe: 'text-block' },
        `Exercice servi : ${d.meta.exerciceCourant}, arrêté au ${d.meta.dateArrete}. ` +
          `Version du prévisionnel de référence : ${d.meta.versionPrevisionnel}.`,
      ),
      h('div', { classe: 'barre-actions' }, bouton),
      retourAction,
      corps,
      noteLecture(
        'Aucun secret n’apparaît dans ce journal : les jetons et identifiants sont masqués avant écriture.',
      ),
    ),
  );
}
